// Generic helpers come from the boilerplate packages — don't reimplement them.
// `cn` lives in the UI layer (matches groot's own shell → ui layering).
export { cn } from "@groot/ui/lib/utils";
// `formatBytes` comes from the shell lib (correct browser types — the local
// copy used NodeJS.Timeout, which is wrong in the browser).
export { formatBytes } from "@groot/shell/lib/utils";

// ─── App-specific finance helpers (no package equivalent) ────────────────

// Generate a consistent random amount based on the original amount.
// Same original amount always produces the same demo amount.
export function generateDemoAmount(originalAmount: number): number {
  // Use the original amount as a seed to ensure consistency
  const seed = Math.abs(originalAmount);
  const pseudoRandom = (Math.sin(seed) * 10000) % 1;

  // Generate a random amount between 100 and 50000
  const min = 100;
  const max = 50000;
  const randomAmount = min + pseudoRandom * (max - min);

  // Keep the sign of the original amount
  return originalAmount < 0 ? -randomAmount : randomAmount;
}

// Format currency (INR) with optional demo mode support.
// Non-React boundary: JSX must render <AmountDisplay> (which reads demo mode
// itself); this function is for jobs, scripts and other non-React contexts only
// — enforced by `node scripts/check-amounts.ts` (check:amounts).
export function formatCurrency(amount: number, currency = "INR", isDemoMode = false): string {
  const displayAmount = isDemoMode ? generateDemoAmount(amount) : amount;
  // eslint-disable-next-line no-restricted-properties -- single implementation site
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
  }).format(displayAmount);
}

// Cards and loans store what is owed as a positive `currentBalance`.
const LIABILITY_ACCOUNT_TYPES: ReadonlySet<string> = new Set(["credit_card", "loan"]);

// An account's balance as it counts toward net worth: liabilities subtract.
export function netWorthContribution(account: {
  type: string;
  currentBalance?: number | null;
}): number {
  const balance = account.currentBalance || 0;
  return LIABILITY_ACCOUNT_TYPES.has(account.type) ? -balance : balance;
}

// Re-export all date utilities from centralized location.
// Always use these instead of creating one-off date formatting functions.
export {
  formatDate,
  formatDisplayDate,
  formatDisplayDateTime,
  formatDetailedDateTime,
  startOfMonthFormatted,
  endOfMonthFormatted,
} from "./date.utils";

// Re-export the live design tokens for convenience.
export { accountTypeLabels, transactionColors, statusColors } from "./design-tokens";
